import { PPO } from '../common/constants/ppoConstants';

/**
 * Computes the playground filename from an original eNO1 filename.
 *
 * The filename format is: `{PPO1}_{PPO2}_{yearQuarter}_{version}_{timestamp}.csv`
 * This function replaces the PPO segment (within the first two parts) that does
 * NOT match the logged-in user's designated operator with "PLG", and appends
 * "_draft" before the ".csv" extension.
 *
 * @param fileName  - The original filename (e.g. "CHA_DKA_20242_00_20240814075051.csv")
 * @param userPpo   - The current user's designated operator code (e.g. "CHA")
 * @param addDraft  - Whether to append "_draft" before the ".csv" extension (default: true)
 * @returns The playground filename (e.g. "CHA_PLG_20242_00_20240814075051_draft.csv")
 */
export function computePlaygroundFilename(fileName: string, userPpo: string, addDraft = true): string {
  const parts = fileName.split('_');

  const replaced = parts
    .map((part, idx) => (idx < 2 && part !== userPpo ? PPO.PLG : part))
    .join('_')
    .replace(/_draft\.csv$/i, '.csv');   // strip existing _draft suffix if present

  return addDraft ? replaced.replace(/\.csv$/i, '_draft.csv') : replaced;
}

/**
 * Determines whether an eNO1 file belongs to the Playground.
 *
 * A file is considered a playground file when either its inbound or
 * outbound PPO equals the PLG constant.
 *
 * @param file - Any object with inbound/outbound PPO fields (ENO1File, ENO1FileDetails, etc.)
 * @returns `true` if the file is a playground file
 */
export function isPlaygroundFile(file: {
  operatorByInboundPpo: string | null;
  operatorByOutboundPpo: string | null;
}): boolean {
  return !!(
    file.operatorByInboundPpo?.startsWith(PPO.PLG) ||
    file.operatorByOutboundPpo?.startsWith(PPO.PLG)
  );
}

/**
 * Known HTTP error status strings returned by the backend in `response.data.status`.
 */
const PLAYGROUND_ERROR_STATUS_KEYS: Record<string, string> = {
  CONFLICT: 'eno1.copyToPlayground.error.CONFLICT',
  PLG: 'eno1.copyToPlayground.error.PLG',
  DATA_VALIDATION: 'eno1.copyToPlayground.error.VALIDATION',
  BAD_REQUEST: 'eno1.copyToPlayground.error.BAD_REQUEST',
  NOT_FOUND: 'eno1.copyToPlayground.error.NOT_FOUND',
  FORBIDDEN: 'eno1.copyToPlayground.error.FORBIDDEN',
  UPGRADE_REQUIRED: 'eno1.copyToPlayground.error.UPGRADE_REQUIRED',
  UNAUTHORIZED: 'eno1.copyToPlayground.error.UNAUTHORIZED',
  INTERNAL_SERVER_ERROR: 'eno1.copyToPlayground.error.INTERNAL_SERVER_ERROR',
};

/**
 * Default (fallback) messages for each known status, used when the locale key
 * is missing.  Kept in sync with the keys above.
 */
const PLAYGROUND_ERROR_DEFAULTS: Record<string, string> = {
  CONFLICT: 'A playground file with the same name already exists. Please remove it before copying again.',
  PLG: 'A playground cannot be created from another playground file.',
  DATA_VALIDATION: 'The server rejected the file due to validation errors. Please verify the file and try again.',
  BAD_REQUEST: 'The server rejected the request. Please verify the file and try again.',
  NOT_FOUND: 'The source file could not be found on the server.',
  FORBIDDEN: 'You do not have permission to perform this operation.',
  UPGRADE_REQUIRED: 'This operation is not enabled system-wide.',
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  INTERNAL_SERVER_ERROR: 'An unexpected server error occurred. Please try again later.',
};

/**
 * Extracts a user-friendly, localised error message from an error thrown
 * during a playground copy operation (download or upload).
 *
 * The function inspects the enriched error object thrown by `useFileUpload`
 * (`{ originalError }`) as well as plain Axios errors, looking for
 * `originalError.response.data.status` (e.g. `"CONFLICT"`).
 *
 * @param error    - The caught error (Axios error or enriched upload error)
 * @param t        - The i18next `t` function for translation
 * @param fallback - A fallback message if no status can be determined
 * @returns A human-readable error string
 */
/**
 * Safely parses the Axios response data, which may be an ArrayBuffer
 * (e.g. when the request used `responseType: 'arraybuffer'`) or a
 * plain object.  Returns the parsed object, or `undefined` on failure.
 */
function parseResponseData(data: unknown): Record<string, any> | undefined {
  if (!data) return undefined;

  // Already a plain object — return as-is
  if (typeof data === 'object' && !(data instanceof ArrayBuffer)) {
    return data as Record<string, any>;
  }

  // ArrayBuffer → decode as UTF-8 text → parse JSON
  if (data instanceof ArrayBuffer) {
    try {
      const text = new TextDecoder('utf-8').decode(data);
      const parsed = JSON.parse(text);
      return typeof parsed === 'object' ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  return undefined;
}

export function getPlaygroundErrorMessage(
  error: unknown,
  t: (key: string, defaultValue: string) => string,
  fallback: string,
): string {
  const err = error as any;

  // The upload hook wraps the original Axios error inside `originalError`
  const axiosError = err?.originalError ?? err;
  const responseData = parseResponseData(axiosError?.response?.data);
  const isFromPLG = err.fromPlayground || false;
  let status: string | undefined = isFromPLG ? "PLG" : responseData?.status || axiosError?.response?.status;
  if (responseData?.errorResponse?.errors?.length) {
    status = "DATA_VALIDATION";
  }
  const serverMessage: string | undefined = responseData?.message;

  if (status && PLAYGROUND_ERROR_STATUS_KEYS[status]) {
    return t(
      PLAYGROUND_ERROR_STATUS_KEYS[status],
      PLAYGROUND_ERROR_DEFAULTS[status] ?? fallback,
    );
  }

  // If the server provided a message but no mapped status, use it directly
  if (serverMessage) {
    return serverMessage;
  }

  return fallback;
}
